import { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import Toast from '../components/ui/Toast';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

/** Botón dentro del toast; al pulsarlo se ejecuta y el toast se cierra. */
export interface ToastAction {
    label: string;
    onClick: () => void;
}

export interface ToastOptions {
    action?: ToastAction;
    duration?: number;
}

export interface ToastState extends ToastOptions {
    id: string;
    message: string;
    type: ToastType;
}

interface ToastContextType {
    showToast: (message: string, type?: ToastType, options?: ToastOptions) => void;
    showSuccess: (message: string) => void;
    showError: (message: string) => void;
    showWarning: (message: string) => void;
    showInfo: (message: string, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<ToastState[]>([]);

    const showToast = useCallback((message: string, type: ToastType = 'info', options?: ToastOptions) => {
        const id = `toast-${Date.now()}-${Math.random()}`;
        setToasts((prev) => [...prev, { id, message, type, ...options }]);
    }, []);

    const showSuccess = useCallback((message: string) => {
        showToast(message, 'success');
    }, [showToast]);

    const showError = useCallback((message: string) => {
        showToast(message, 'error');
    }, [showToast]);

    const showWarning = useCallback((message: string) => {
        showToast(message, 'warning');
    }, [showToast]);

    const showInfo = useCallback((message: string, options?: ToastOptions) => {
        showToast(message, 'info', options);
    }, [showToast]);

    const hideToast = useCallback((id: string) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ showToast, showSuccess, showError, showWarning, showInfo }}>
            {children}
            {toasts.map((toast, index) => (
                <Toast 
                    key={toast.id}
                    id={toast.id}
                    message={toast.message}
                    type={toast.type}
                    action={toast.action}
                    duration={toast.duration}
                    onClose={hideToast}
                    index={index}
                />
            ))}
        </ToastContext.Provider>
    );
}

export function useToast() {
    const context = useContext(ToastContext);
    if (context === undefined) {
        throw new Error('useToast debe ser usado dentro de un ToastProvider');
    }
    return context;
}
